import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Store for managing Google Calendar sync state
 * Tracks sync tokens, status, and pending changes for two-way sync
 */
export const useGoogleSyncStore = create(
    persist(
        (set, get) => ({
            // Sync tokens per calendar (for incremental sync)
            syncTokens: {}, // { 'accountEmail-calendarId': syncToken }

            // Last sync time per calendar
            lastSyncTimes: {}, // { 'accountEmail-calendarId': timestamp }

            // Sync status: 'idle' | 'syncing' | 'error' | 'conflict'
            syncStatus: 'idle',

            // Sync error message if any
            syncError: null,

            // Pending local changes that need to be pushed to Google
            pendingChanges: [], // [{ type: 'create'|'update'|'delete', event, timestamp }]

            // Conflicts detected during sync
            conflicts: [], // [{ localEvent, remoteEvent, detectedAt }]

            // Events with their sync metadata
            eventSyncMeta: {}, // { eventId: { etag, lastModified, syncedAt, source: 'local'|'google' } }

            // Auto-sync enabled (default false to prevent duplicate events on initial load)
            autoSyncEnabled: false,

            // Auto-sync interval in ms (default 5 minutes)
            autoSyncInterval: 5 * 60 * 1000,

            // ============ SYNC TOKEN MANAGEMENT ============

            setSyncToken: (accountEmail, calendarId, token) => {
                const key = `${accountEmail}-${calendarId}`;
                set((state) => ({
                    syncTokens: { ...state.syncTokens, [key]: token }
                }));
            },

            getSyncToken: (accountEmail, calendarId) => {
                const key = `${accountEmail}-${calendarId}`;
                return get().syncTokens[key] || null;
            },

            clearSyncToken: (accountEmail, calendarId) => {
                const key = `${accountEmail}-${calendarId}`;
                set((state) => {
                    const { [key]: _, ...rest } = state.syncTokens;
                    return { syncTokens: rest };
                });
            },

            clearAllSyncTokens: () => {
                set({ syncTokens: {} });
            },

            // ============ SYNC STATUS MANAGEMENT ============

            setSyncStatus: (status, error = null) => {
                set({ syncStatus: status, syncError: error });
            },

            setLastSyncTime: (accountEmail, calendarId) => {
                const key = `${accountEmail}-${calendarId}`;
                set((state) => ({
                    lastSyncTimes: { ...state.lastSyncTimes, [key]: Date.now() }
                }));
            },

            getLastSyncTime: (accountEmail, calendarId) => {
                const key = `${accountEmail}-${calendarId}`;
                return get().lastSyncTimes[key] || null;
            },

            // ============ PENDING CHANGES (for offline support) ============

            addPendingChange: (type, event) => {
                set((state) => ({
                    pendingChanges: [
                        ...state.pendingChanges,
                        { type, event, timestamp: Date.now(), id: `${type}-${event.id}-${Date.now()}` }
                    ]
                }));
            },

            removePendingChange: (changeId) => {
                set((state) => ({
                    pendingChanges: state.pendingChanges.filter(c => c.id !== changeId)
                }));
            },

            clearPendingChanges: () => {
                set({ pendingChanges: [] });
            },

            getPendingChangesForEvent: (eventId) => {
                return get().pendingChanges.filter(c => c.event.id === eventId);
            },

            // ============ CONFLICT MANAGEMENT ============

            addConflict: (localEvent, remoteEvent) => {
                set((state) => ({
                    conflicts: [
                        ...state.conflicts,
                        {
                            localEvent,
                            remoteEvent,
                            detectedAt: Date.now(),
                            id: `conflict-${localEvent.id}-${Date.now()}`
                        }
                    ],
                    syncStatus: 'conflict'
                }));
            },

            resolveConflict: (conflictId, resolution) => {
                // resolution: 'local' | 'remote' | 'merge'
                set((state) => ({
                    conflicts: state.conflicts.filter(c => c.id !== conflictId),
                    syncStatus: state.conflicts.length <= 1 ? 'idle' : 'conflict'
                }));
                return resolution;
            },

            clearConflicts: () => {
                set({ conflicts: [], syncStatus: 'idle' });
            },

            // ============ EVENT SYNC METADATA ============

            setEventSyncMeta: (eventId, meta) => {
                set((state) => ({
                    eventSyncMeta: {
                        ...state.eventSyncMeta,
                        [eventId]: { ...state.eventSyncMeta[eventId], ...meta, syncedAt: Date.now() }
                    }
                }));
            },

            getEventSyncMeta: (eventId) => {
                return get().eventSyncMeta[eventId] || null;
            },

            removeEventSyncMeta: (eventId) => {
                set((state) => {
                    const { [eventId]: _, ...rest } = state.eventSyncMeta;
                    return { eventSyncMeta: rest };
                });
            },

            // ============ AUTO-SYNC SETTINGS ============

            setAutoSyncEnabled: (enabled) => {
                set({ autoSyncEnabled: enabled });
            },

            setAutoSyncInterval: (interval) => {
                set({ autoSyncInterval: interval });
            },

            // ============ RESET ============

            resetSyncState: () => {
                set({
                    syncTokens: {},
                    lastSyncTimes: {},
                    syncStatus: 'idle',
                    syncError: null,
                    pendingChanges: [],
                    conflicts: [],
                    eventSyncMeta: {}
                });
            }
        }),
        {
            name: 'google-sync-storage',
            partialize: (state) => ({
                syncTokens: state.syncTokens,
                lastSyncTimes: state.lastSyncTimes,
                eventSyncMeta: state.eventSyncMeta,
                autoSyncEnabled: state.autoSyncEnabled,
                autoSyncInterval: state.autoSyncInterval
            })
        }
    )
);

export default useGoogleSyncStore;
